import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import Calendar from '../../components/Calendar';
import Modal from '../../components/Modal';
import './Calendar.css';

const AdminCalendar = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedLocation } = useOutletContext() || {};
  const { data, getCourseById, addSchedule, addCalendarEvent } = useData();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    eventType: 'lesson',
    title: '',
    description: '',
    courseId: '',
    date: '',
    startTime: '09:00',
    endTime: '17:00',
    location: user?.location || 'Paramaribo'
  });

  // Build events from schedules
  const getEvents = () => {
    return (data.schedules || []).map((schedule) => {
      const course = getCourseById(schedule.courseId);
      const start = schedule.start || schedule.startDateTime || (schedule.date && schedule.startTime ? `${schedule.date}T${schedule.startTime}` : null);
      const end = schedule.end || schedule.endDateTime || (schedule.date && schedule.endTime ? `${schedule.date}T${schedule.endTime}` : null);

      return {
        id: schedule.id,
        title: schedule.title || (course ? `${course.code} - ${course.name}` : ''),
        start,
        end,
        location: schedule.location,
        courseId: schedule.courseId,
        type: schedule.type || schedule.eventType || 'lesson',
        description: schedule.description || '',
        extendedProps: {
          course,
          schedule
        }
      };
    }).filter((event) => event.start && event.end);
  };

  const handleEventClick = (info) => {
    setSelectedEvent(info.event);
  };

  const handleAddSchedule = (e) => {
    e.preventDefault();
    if (!newSchedule.date) return;

    const startDateTime = `${newSchedule.date}T${newSchedule.startTime}`;
    const endDateTime = `${newSchedule.date}T${newSchedule.endTime}`;

    if (newSchedule.eventType === 'lesson') {
      if (!newSchedule.courseId) return;
      addSchedule(newSchedule.courseId, {
        title: newSchedule.title,
        description: newSchedule.description,
        date: newSchedule.date,
        startTime: newSchedule.startTime,
        endTime: newSchedule.endTime,
        location: newSchedule.location,
        eventType: 'lesson'
      });
    } else {
      addCalendarEvent({
        courseId: newSchedule.courseId || undefined,
        title: newSchedule.title || (newSchedule.eventType === 'activity' ? 'Activiteit' : 'Evenement'),
        description: newSchedule.description,
        startDateTime,
        endDateTime,
        location: newSchedule.location,
        eventType: newSchedule.eventType
      });
    }

    setNewSchedule({
      eventType: 'lesson',
      title: '',
      description: '',
      courseId: '',
      date: '',
      startTime: '09:00',
      endTime: '17:00',
      location: user?.location || 'Paramaribo'
    });
    setShowAddForm(false);
  };

  // Get courses for dropdown with role/location filtering
  const allCourses = (data.courses || []).filter((course) => {
    const courseLocation = (course.location || '').toLowerCase();

    if (user?.role === 'manager' && user?.location) {
      return courseLocation === String(user.location).toLowerCase();
    }

    if (user?.role === 'instructor') {
      return String(course.instructor?.id || course.instructorId || '') === String(user.id || user._id || '');
    }

    if (selectedLocation && selectedLocation !== 'all') {
      return courseLocation === String(selectedLocation).toLowerCase();
    }

    return true;
  });

  return (
    <div className="admin-calendar-page">
      <div className="page-header">
        <h1>{t('admin.calendar')}</h1>
        <button className="btn-primary" onClick={() => setShowAddForm(!showAddForm)}>
          {t('calendar.addEvent')}
        </button>
      </div>

      <Modal isOpen={showAddForm} onClose={() => setShowAddForm(false)} title={t('calendar.addEvent')}>
          <form onSubmit={handleAddSchedule} className="schedule-form">
            <select
              value={newSchedule.eventType}
              onChange={(e) => setNewSchedule({ ...newSchedule, eventType: e.target.value })}
              className="form-select"
            >
              <option value="lesson">Les</option>
              <option value="event">Evenement</option>
              <option value="activity">Activiteit</option>
            </select>

            <input
              type="text"
              value={newSchedule.title}
              onChange={(e) => setNewSchedule({ ...newSchedule, title: e.target.value })}
              className="form-input"
              placeholder="Titel"
            />

            <input
              type="text"
              value={newSchedule.description}
              onChange={(e) => setNewSchedule({ ...newSchedule, description: e.target.value })}
              className="form-input"
              placeholder={t('course.description')}
            />

            <select
              value={newSchedule.courseId}
              onChange={(e) => setNewSchedule({ ...newSchedule, courseId: e.target.value })}
              className="form-select"
              disabled={newSchedule.eventType === 'activity' || newSchedule.eventType === 'event'}
            >
              <option value="">-- {t('enrollment.course')} ({t('common.optional')}) --</option>
              {allCourses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.code} - {course.name}
                </option>
              ))}
            </select>
            
            <input
              type="date"
              value={newSchedule.date}
              onChange={(e) => setNewSchedule({ ...newSchedule, date: e.target.value })}
              className="form-input"
            />
            
            <input
              type="time"
              value={newSchedule.startTime}
              onChange={(e) => setNewSchedule({ ...newSchedule, startTime: e.target.value })}
              className="form-input"
              placeholder={t('studyYear.startDate')}
            />
            
            <input
              type="time"
              value={newSchedule.endTime}
              onChange={(e) => setNewSchedule({ ...newSchedule, endTime: e.target.value })}
              className="form-input"
              placeholder={t('studyYear.endDate')}
            />
            
            <input
              type="text"
              value={newSchedule.location}
              onChange={(e) => setNewSchedule({ ...newSchedule, location: e.target.value })}
              className="form-input"
              placeholder={t('course.location')}
            />
            
            <button type="submit" className="btn-primary">{t('common.save')}</button>
          </form>
      </Modal>

      <Calendar 
        events={getEvents()} 
        onEventClick={handleEventClick}
        editable={true}
      />

      {selectedEvent && (
        <div className="event-details">
          <h3>Event Details</h3>
          <p><strong>Titel:</strong> {selectedEvent.title}</p>
          <p><strong>Type:</strong> {selectedEvent.extendedProps?.schedule?.eventType || selectedEvent.extendedProps?.schedule?.type || 'lesson'}</p>
          <p><strong>Beschrijving:</strong> {selectedEvent.extendedProps?.schedule?.description || '-'}</p>
          <p><strong>Locatie:</strong> {selectedEvent.extendedProps?.schedule?.location}</p>
          <p><strong>Tijd:</strong> {new Date(selectedEvent.start).toLocaleString()} - {new Date(selectedEvent.end).toLocaleString()}</p>
          <button className="btn-secondary" onClick={() => setSelectedEvent(null)}>Sluiten</button>
        </div>
      )}
    </div>
  );
};

export default AdminCalendar;
